import { type ReactNode } from "react"
import { Outlet, useLocation } from "react-router"
import HardRefresh from "source/shared/HardRefresh"
import {
  GivingOrganizationProvider,
  givingOrganizationQuery,
} from "./profile/hooks/useGivingOrganizationContext"
import { FlagsProvider, useFlagsContext } from "./hooks/useFlagsContext"
import { fetchFlagsQuery } from "./queries"
import { useGivingPreviewModeAccess } from "./hooks/useGivingPreviewModeAccess"
import { queryClient } from "source/ReactQueryProvider"
import { PublishedPageGuard } from "source/shared/guards/PublishedPageGuard"
import { Error404 } from "source/shared/components/Error404"
import { Loading } from "source/shared/components"

export async function clientLoader() {
  queryClient.prefetchQuery(givingOrganizationQuery())
  queryClient.prefetchQuery(fetchFlagsQuery())
}

export default function GivingLayout() {
  return (
    <GivingOrganizationProvider>
      <GivingPublishedPageGuard>
        <FlagsProvider>
          <GivingGuard />
        </FlagsProvider>
      </GivingPublishedPageGuard>
    </GivingOrganizationProvider>
  )
}

function GivingPublishedPageGuard({ children }: { children: ReactNode }) {
  return (
    <PublishedPageGuard
      identifier="giving"
      fallback={<PreviewModeAccessGuard>{children}</PreviewModeAccessGuard>}
    >
      {children}
    </PublishedPageGuard>
  )
}

function PreviewModeAccessGuard({ children }: { children: ReactNode }) {
  const { isLoading, hasPreviewModeAccess } = useGivingPreviewModeAccess({
    enabled: true,
  })

  if (isLoading) return <Loading message="Loading" />
  if (!hasPreviewModeAccess) return <Error404 />

  return <>{children}</>
}

function GivingGuard() {
  const { flags } = useFlagsContext()

  const { search } = useLocation()
  const urlParams = new URLSearchParams(search)
  if (urlParams.get("app_guest_mode") === "true") {
    sessionStorage.setItem("app_guest_mode", "true")
  }

  if (
    !flags.monorepo_donation_form &&
    !flags.monorepo_my_giving &&
    !flags.monorepo_text2give
  ) {
    return <HardRefresh />
  }

  return <Outlet />
}
